import { useMemo, useState } from "react";

import {
  Box,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CheckroomOutlinedIcon from "@mui/icons-material/CheckroomOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { IEventoResponse } from "interfaces/eventos";
import {
  createTamanhoCamiseta,
  getTamanhoCamisetaDefault,
} from "services/kitsCamiseta";
import { ICamiseta } from "interfaces/kitsCamiseta";

import CardCamisetas from "./componets/CardCamisetas";
import ButtonPlus from "./componets/ButtonPlus";
import ModalAddEditKits from "./componets/ModalAddEditKits";
import {
  KITS_ACCENT,
  kitsSectionSx,
  kitsToolbarSx,
} from "./kits-evento.theme";

interface KitsProps {
  evento: IEventoResponse;
  updateEvento: () => void;
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={accent ? KITS_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function Kits({ evento, updateEvento }: KitsProps) {
  const [openModal, setOpenModal] = useState(false);
  const [kitSelected, setKitSelected] = useState<ICamiseta | null>(null);
  const [loadingPadrao, setLoadingPadrao] = useState(false);

  const listTamanhoCamiseta = evento.camisetas ?? [];

  const stats = useMemo(() => {
    const totalPecas = listTamanhoCamiseta.reduce(
      (sum, item) => sum + (item.quantidade ?? 0),
      0,
    );
    const media =
      listTamanhoCamiseta.length > 0
        ? Math.round(totalPecas / listTamanhoCamiseta.length)
        : 0;
    return {
      tamanhos: listTamanhoCamiseta.length,
      totalPecas,
      media,
    };
  }, [listTamanhoCamiseta]);

  const handleGetCamisetaDefault = async () => {
    setLoadingPadrao(true);
    try {
      const camisetaDefault = await getTamanhoCamisetaDefault(evento.id);
      await createTamanhoCamiseta(camisetaDefault);
      updateEvento();
    } finally {
      setLoadingPadrao(false);
    }
  };

  const abrirModal = (kit: ICamiseta | null = null) => {
    setKitSelected(kit);
    setOpenModal(true);
  };

  const fecharModal = () => {
    setOpenModal(false);
    setKitSelected(null);
  };

  return (
    <MDBox>
      <Box sx={kitsToolbarSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Inventory2OutlinedIcon sx={{ color: KITS_ACCENT }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Kits do evento
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {evento.titulo}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {listTamanhoCamiseta.length === 0 ? (
              <MDButton
                variant="outlined"
                color="dark"
                onClick={() => void handleGetCamisetaDefault()}
                disabled={loadingPadrao}
                sx={{ flexShrink: 0 }}
              >
                {loadingPadrao ? "Carregando…" : "Carregar padrão"}
              </MDButton>
            ) : null}
            <MDButton
              variant="gradient"
              color="dark"
              onClick={() => abrirModal(null)}
              startIcon={<AddOutlinedIcon />}
              sx={{
                flexShrink: 0,
                background: `linear-gradient(195deg, ${KITS_ACCENT}, #e04a1a)`,
              }}
            >
              Novo tamanho
            </MDButton>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={6} sm={4}>
          <StatCard label="Tamanhos cadastrados" value={stats.tamanhos} accent />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatCard label="Total de peças" value={stats.totalPecas} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Média por tamanho" value={stats.media} />
        </Grid>
      </Grid>

      <Box sx={kitsSectionSx}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          mb={2}
        >
          <CheckroomOutlinedIcon sx={{ fontSize: 20, color: KITS_ACCENT }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Camisetas por tamanho
          </Typography>
        </Stack>

        {listTamanhoCamiseta.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 2,
              textAlign: "center",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "grey.300",
              bgcolor: "grey.50",
            }}
          >
            <CheckroomOutlinedIcon
              sx={{ fontSize: 44, color: "grey.400", mb: 1 }}
            />
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Nenhum tamanho cadastrado
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Carregue os tamanhos padrão ou adicione manualmente as camisetas
              do kit.
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="center"
            >
              <MDButton
                variant="outlined"
                color="dark"
                onClick={() => void handleGetCamisetaDefault()}
                disabled={loadingPadrao}
              >
                {loadingPadrao ? "Carregando…" : "Carregar padrão"}
              </MDButton>
              <MDButton
                variant="gradient"
                color="dark"
                onClick={() => abrirModal(null)}
                startIcon={<AddOutlinedIcon />}
                sx={{
                  background: `linear-gradient(195deg, ${KITS_ACCENT}, #e04a1a)`,
                }}
              >
                Adicionar tamanho
              </MDButton>
            </Stack>
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
          >
            {listTamanhoCamiseta.map((item) => (
              <CardCamisetas
                key={item.id ?? item.tamanho}
                item={item}
                updateEvento={updateEvento}
                openModalAddEdit={abrirModal}
              />
            ))}
            <ButtonPlus openModalAddEdit={abrirModal} />
          </Box>
        )}
      </Box>

      <ModalAddEditKits
        evento={evento}
        kit={kitSelected}
        open={openModal}
        updateEvento={updateEvento}
        handleClose={fecharModal}
      />
    </MDBox>
  );
}
